import { useGuestPayorPaymentRequestDetails } from '@melio/ar-domain';
import { useToast } from '@melio/penny';
import { PlaidAccountData, useFundingSource, useFundingSources } from '@melio/platform-api';
import { forwardRef, useBoolean } from '@melio/platform-utils';
import React, { useState } from 'react';

import { NewGuestPaymentLayout } from '../../layout';
import { DeleteBankAccountModal } from './components';
import { AddPlaidFundingSourceScreen, GuestPaymentErrorScreen, GuestPaymentRequestDetailsScreen } from './screens';

export type GuestPaymentRequestDetailsActivityProps = {
  onDone: VoidFunction;
  onBack?: VoidFunction;
  onClose?: VoidFunction;
  onError?: ErrorFunction;
  onViewInvoice: VoidFunction;
  paymentRequestLink: string;
};

export const GuestPaymentRequestDetailsActivity = forwardRef<GuestPaymentRequestDetailsActivityProps>(
  ({ onViewInvoice, onDone, paymentRequestLink, ...props }, ref) => {
    const {
      data: paymentRequestDetails,
      isLoading,
      error,
    } = useGuestPayorPaymentRequestDetails({ paymentRequestLink });
    const toast = useToast();
    const { create } = useFundingSources({ enabled: false });
    const [isPlaidModalOpen, openPlaidModal] = useBoolean(false);
    const [fundingSourceId, setFundingSourceId] = useState<string>();
    const { data: fundingSource, isLoading: isLoadingFundingSource } = useFundingSource({ id: fundingSourceId });
    const [isDeleteBankAccountModalOpen, deleteBankAccountModal] = useBoolean(false);
    const [isLoadingPlaidToken, loadingPlaidToken] = useBoolean(false);
    const isSubmitting = isLoadingFundingSource || isLoadingPlaidToken;

    if (isLoading) {
      return (
        <NewGuestPaymentLayout
          isLoading
          data-component={GuestPaymentRequestDetailsActivity.displayName}
          {...props}
          ref={ref}
        />
      );
    }

    if (error || !paymentRequestDetails) {
      return <GuestPaymentErrorScreen errorType="network-error" />;
    }

    const createPlaidFundingSource = (data: PlaidAccountData) => {
      openPlaidModal.off();
      void create({
        type: 'plaid',
        details: { plaidAccountId: data.accounts[0]?.id as string, plaidToken: data.public_token },
      }).then(({ id }) => setFundingSourceId(id));
    };

    const plaidErrorHandler = (error: PlatformError) => {
      openPlaidModal.off();
      toast({ type: 'error', title: error.message });
    };

    return (
      <>
        <GuestPaymentRequestDetailsScreen
          ref={ref}
          {...props}
          payeePaymentRequestDetails={paymentRequestDetails.payeeDetails}
          invoicePaymentRequestDetails={{
            totalAmount: paymentRequestDetails.invoice.totalAmount,
            dueDate: paymentRequestDetails.invoice.dueDate,
            previewUrls: paymentRequestDetails.invoice.fileInfo.previewUrls,
            paymentOptions: paymentRequestDetails.invoice.paymentOptions,
          }}
          onViewInvoice={onViewInvoice}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
          onAddPlaidAccount={() => openPlaidModal.on()}
          fundingSourceName={fundingSource?.displayName}
          onDeleteBankAccount={() => deleteBankAccountModal.on()}
        />
        {isPlaidModalOpen ? (
          <AddPlaidFundingSourceScreen
            onLoadToken={(isLoading) => {
              if (isLoading) {
                loadingPlaidToken.on();
              } else {
                loadingPlaidToken.off();
              }
            }}
            onSuccess={createPlaidFundingSource}
            onClose={() => openPlaidModal.off()}
            onError={plaidErrorHandler}
          />
        ) : null}
        <DeleteBankAccountModal
          isOpen={isDeleteBankAccountModalOpen}
          fundingSourceName={fundingSource?.displayName || ''}
          onSubmit={() => {
            setFundingSourceId('');
            deleteBankAccountModal.off();
          }}
          onClose={() => {
            deleteBankAccountModal.off();
          }}
        />
      </>
    );
  }
);
GuestPaymentRequestDetailsActivity.displayName = 'GuestPaymentRequestDetailsActivity';
